// Notification utility for consistent form feedback

/**
 * Show a success notification
 * @param {string} message
 * @param {number} duration - Duration in milliseconds (default: 5000)
 */
export function showSuccessNotification(message, duration = 5000) {
    createNotification(message, 'success', duration);
}

/**
 * Show an error notification
 * @param {string} message
 * @param {number} duration - Duration in milliseconds (default: 5000)
 */
export function showErrorNotification(message, duration = 5000) {
    createNotification(message, 'error', duration);
}

/**
 * Show a warning notification for mock mode
 * @param {string} message
 * @param {number} duration - Duration in milliseconds (default: 8000)
 */
export function showMockWarningNotification(message, duration = 8000) {
    createNotification(message, 'warning', duration);
}

/**
 * Create and display a notification
 * @param {string} message
 * @param {string} type - 'success', 'error', or 'warning'
 * @param {number} duration
 */
function createNotification(message, type, duration) {
    // Remove any existing notifications
    const existingNotifications = document.querySelectorAll('.custom-notification');
    existingNotifications.forEach(notification => notification.remove());

    // Get icon based on type
    let icon = 'fa-exclamation-circle';
    if (type === 'success') icon = 'fa-check-circle';
    else if (type === 'warning') icon = 'fa-exclamation-triangle';

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `custom-notification ${type}`;
    notification.innerHTML = `
        <div class="notification-content">
            <i class="fas ${icon}"></i>
            <span class="notification-message">${message}</span>
            <button class="notification-close" onclick="this.parentElement.parentElement.remove()">
                <i class="fas fa-times"></i>
            </button>
        </div>
    `;

    // Add styles
    addNotificationStyles();

    // Add to page
    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => {
        notification.classList.add('show');
    }, 100);

    // Auto remove after duration
    setTimeout(() => {
        if (notification.parentElement) {
            notification.classList.remove('show');
            setTimeout(() => {
                notification.remove();
            }, 300);
        }
    }, duration);
}

/**
 * Add notification styles to the page
 */
function addNotificationStyles() {
    // Check if styles already exist
    if (document.getElementById('notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'notification-styles';
    style.textContent = `
        .custom-notification {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 10000;
            min-width: 320px;
            max-width: 500px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            transform: translateX(100%);
            transition: all 0.3s ease;
            border-left: 4px solid;
            font-family: 'Poppins', sans-serif;
        }

        .custom-notification.success {
            border-left-color: #28a745;
        }

        .custom-notification.error {
            border-left-color: #dc3545;
        }

        .custom-notification.warning {
            border-left-color: #ffc107;
        }

        .custom-notification.show {
            transform: translateX(0);
        }

        .notification-content {
            display: flex;
            align-items: center;
            padding: 16px 20px;
            gap: 12px;
        }

        .custom-notification.success .fas {
            color: #28a745;
        }

        .custom-notification.error .fas {
            color: #dc3545;
        }

        .custom-notification.warning .fas {
            color: #ffc107;
        }

        .notification-message {
            flex: 1;
            color: #333;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.4;
        }

        .notification-close {
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s ease;
        }

        .notification-close:hover {
            background-color: #f5f5f5;
            color: #333;
        }

        @media (max-width: 768px) {
            .custom-notification {
                right: 10px;
                left: 10px;
                min-width: auto;
                max-width: none;
            }
        }
    `;
    document.head.appendChild(style);
}

/**
 * Clear all form fields
 * @param {HTMLFormElement} form
 */
export function clearFormFields(form) {
    if (!form) return;

    const inputs = form.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = false;
        } else {
            input.value = '';
        }

        // Remove any validation classes
        input.classList.remove('error', 'success');
        
        // Remove focused class from parent
        const formGroup = input.closest('.form-group');
        if (formGroup) {
            formGroup.classList.remove('focused');
        }

        // Remove any error messages
        const errorMessage = input.parentElement.querySelector('.error-message');
        if (errorMessage) {
            errorMessage.remove();
        }
    });
}
